import React, { useState } from "react"; // Removed useEffect as it wasn't used for lang anymore
import { Nav, Button, Image, Dropdown } from "react-bootstrap"; // Removed unused imports
import { HiOutlineHome } from "react-icons/hi2";
import { PiBriefcase } from "react-icons/pi";
import { HiOutlineClipboardDocumentCheck } from "react-icons/hi2";
import {
  FaAngleLeft,
  FaAngleRight
} from "react-icons/fa";
import { GoQuestion } from "react-icons/go";
import { useType } from "../context/TypeContext";
import "../styling/SideBar.css";
import { useEnv } from "../context/EnvironmentContext";
import { useUserManager } from "../context/UserManagerContext";
// Import the new Language Hook
import { useLanguage } from "../context/LanguageContext";

interface SidebarProps {
  expanded: boolean;
  username: string;
  handleLogout: () => void;
  toggleSidebar: () => void;
  defaultPortal: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  expanded,
  username,
  handleLogout,
  toggleSidebar,
  defaultPortal
}) => {
  const { env } = useEnv(); // Removed setEnv if not used here
  const { userManager } = useUserManager(); // Kept for reference
  const { type, variant, setType, setVariant } = useType();

  // Use Global Language Context
  const { isSwedish, toggleLanguage } = useLanguage();

  const [caseListOpen, setCaseListOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Removed local state:
  // const [swedishTranslationOn, setSwedishTranslation] = useState(true)

  const [caseItems, setCaseItems] = useState<string[]>([]);

  const toggleCaseList = () => {
    if (!expanded) {
      toggleSidebar();
      const timeoutId = setTimeout(() => {
        setCaseListOpen(!caseListOpen);
      }, 100);

      return () => clearTimeout(timeoutId);
    } else {
      setCaseListOpen(!caseListOpen);
    }
  };

  const reloadPega = () => {
    const embed = document.getElementById("theEmbed");
    if (embed) (embed as any).reload();
  };

  const logoutPega = () => {
    const embed = document.getElementById("theEmbed");
    if (embed) (embed as any).logout();
  };

  const setCreateVariant = (caseName: string) => {
    const createVariant = caseName.replace(/\s/g, "");
    if (type === "create" && variant === createVariant) {
      reloadPega();
    } else {
      setType("create");
      setVariant(createVariant);
    }
  };

  const setOpenVariant = (openVariant: string) => {
    if (type === "open" && variant === openVariant) {
      reloadPega();
    } else {
      setType("open");
      setVariant(openVariant);
    }
  };

  // define sidebar labels in english and swedish language. Refer to label variables in function
  const label = {
    home: isSwedish ? "Startsida" : "Home",
    activeCases: isSwedish ? "Mina aktiva ärenden" : "My active cases",
    closedCases: isSwedish ? "Mina beslutade ärenden" : "My decided cases",
    languages: isSwedish ? "Svenska" : "English",
    help: isSwedish ? "Hjälp & stöd" : "Help & support",
    contact: isSwedish ? "Kontakta oss för teknisk support" : "Contact us for technical support",
    userManual: isSwedish ? "Användarmanual" : "User manual",
    profile: isSwedish ? "Min profil" : "My profile",
    logOut: isSwedish ? "Logga ut" : "Log out"
  };

  return (
    <div className={`sidebar ${expanded ? "expanded" : "collapsed"}`}>
      <Nav className="flex-column">
        <Nav.Link
          href="/#"
          className="logo-item no-hover"
          onClick={() => setOpenVariant(
            defaultPortal && defaultPortal.startsWith("Agent") ? "AgentHomePage" : "CandidateHomePage")
          }
        >
          <Image
            src="https://www.fi.se/gfx/images/fi-logotyp-vit.svg"
            alt="Company Logo"
            //height={50}
            className="sidebar-logo"
          //style={{ marginLeft: "-13px" }}
          />
          <span className="logo-text link-text"> FI Ansök</span>
        </Nav.Link>

        <Button
          variant="outline-light"
          className="sidebar-toggle"
          onClick={() => {
            toggleSidebar();
          }}
        >
          {expanded ? <FaAngleLeft /> : <FaAngleRight />}
        </Button>

        <Nav.Link
          href="/#"
          className="nav-item"
          onClick={() => {
            setOpenVariant(defaultPortal && defaultPortal.startsWith("Agent") ? "AgentHomePage" : "CandidateHomePage");
            reloadPega();
          }}
        >
          <HiOutlineHome size="1.25rem" />
          {/* Only render tooltip when sidebar is collapsed */}
          {!expanded && (
            <span className="tooltip">
              {label.home}
            </span>
          )}
          <span className="link-text">{label.home}</span>
        </Nav.Link>

        {defaultPortal && defaultPortal.startsWith("Agent") ? (
          <>
            <Nav.Link
              href="#"
              className="nav-item"
              onClick={() => setOpenVariant("ActiveCasesPage")}
            >
              <PiBriefcase size="1.25rem" />
              {!expanded && (
                <span className="tooltip">
                  {label.activeCases}
                </span>
              )}
              <span className="link-text">{label.activeCases}</span>
            </Nav.Link>

            <Nav.Link
              href="#"
              className="nav-item"
              onClick={() => setOpenVariant("ClosedCasesPage")}
            >
              <HiOutlineClipboardDocumentCheck size="1.25rem" />
              {!expanded && (
                <span className="tooltip">
                  {label.closedCases}
                </span>
              )}
              <span className="link-text">{label.closedCases}</span>
            </Nav.Link>
          </>
        ) : (<></>)}
      </Nav>

      {/* Language Toggle */}
      <div className="language-toggle">
        {/* Swedish */}
        <span
          className={`language-option ${isSwedish ? 'active' : ''}`}
          // Toggle to Swedish only if it is on English
          onClick={!isSwedish ? toggleLanguage : undefined}
        >
          {expanded ? "Svenska" : "SV"}
        </span>

        {/* English */}
        <span
          className={`language-option ${!isSwedish ? 'active' : ''}`}
          onClick={isSwedish ? toggleLanguage : undefined}
        >
          {expanded ? "English" : "EN"}
        </span>
        {/* Tooltip to indicate the current chosen language */}
        {!expanded && (
          <span className="tooltip">{label.languages}</span>
        )}
      </div>

      {/* Help icon */}
      <div className="sidebar-help">
        <Dropdown
          show={helpOpen}
          onToggle={(isOpen) => setHelpOpen(isOpen)}
        >
          <Dropdown.Toggle
            variant="link"
            id="dropdown-help"
            onClick={() => setHelpOpen(!helpOpen)}
          >
            <div className="help-icon">
              <GoQuestion />
              <span className="tooltip">{label.help}</span>
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item href="mailto:support.ansok@fi.se">
              {label.contact}
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item href="https://www.fi.se/contentassets/ef0d077b49c44848b1d98841b90dff37/handbok-e-tjanst-ansok_version-1.0_-ren.pdf">
              {label.userManual}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Profile Dropdown */}
      <div className="sidebar-profile">
        <Dropdown
          show={profileOpen}
          onToggle={(isOpen) => setProfileOpen(isOpen)}
        >
          <Dropdown.Toggle
            variant="link"
            id="dropdown-profile"
            onClick={() => setProfileOpen(!profileOpen)}
          >
            <div className="profile-icon">{username.charAt(0)}</div>
          </Dropdown.Toggle>
          <span className="tooltip">{label.profile}</span>
          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() => {
                // setOpenVariant("ProfilePage");
              }}
            >
              {username}
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              onClick={() => {
                handleLogout();
                logoutPega();
              }}
            >
              {label.logOut}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

export default Sidebar;
